export interface NormalizedAgentErrorDetails {
  message: string;
  type: string | null;
  code: string | null;
}

type PartialNormalizedAgentErrorDetails = {
  message: string | null;
  type: string | null;
  code: string | null;
};

// Providers sometimes wrap the real error inside multiple JSON/string envelopes.
// Five levels handles the nested transport bodies we've observed while keeping
// normalization bounded if an upstream payload is pathological.
const MAX_ERROR_NORMALIZATION_DEPTH = 5;

const STRUCTURED_ERROR_VALUE_KEYS = [
  "error",
  "detail",
  "details",
] as const;

const MESSAGE_ERROR_VALUE_KEYS = [
  "message",
  "reason",
  "title",
] as const;

const ARRAY_ERROR_VALUE_KEYS = ["errors", "causes"] as const;

const NESTED_ERROR_VALUE_KEYS = [
  "data",
  "body",
  "cause",
  "response",
  "payload",
  "meta",
  "result",
] as const;

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function asCodeString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }

  return null;
}

function tryParseEmbeddedJson(value: string): unknown | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    // Fall through to embedded JSON detection.
  }

  const objectIndex = trimmed.indexOf("{");
  const arrayIndex = trimmed.indexOf("[");
  const startIndex =
    objectIndex === -1
      ? arrayIndex
      : arrayIndex === -1
        ? objectIndex
        : Math.min(objectIndex, arrayIndex);
  if (startIndex <= 0) return null;

  try {
    return JSON.parse(trimmed.slice(startIndex));
  } catch {
    return null;
  }
}

function mergeErrorDetails(args: {
  outer: PartialNormalizedAgentErrorDetails;
  inner: PartialNormalizedAgentErrorDetails;
}): PartialNormalizedAgentErrorDetails {
  return {
    message: args.inner.message,
    type: args.inner.type ?? args.outer.type,
    code: args.inner.code ?? args.outer.code,
  };
}

function getErrorDetailsScore(value: PartialNormalizedAgentErrorDetails | null): number {
  if (!value) return -1;

  let score = value.message ? 1 : 0;
  if (value.type) score += 2;
  if (value.code) score += 2;
  return score;
}

function pickBetterErrorDetails(
  left: PartialNormalizedAgentErrorDetails | null,
  right: PartialNormalizedAgentErrorDetails | null,
): PartialNormalizedAgentErrorDetails | null {
  if (!left) return right;
  if (!right) return left;

  const leftScore = getErrorDetailsScore(left);
  const rightScore = getErrorDetailsScore(right);
  if (rightScore > leftScore) return right;
  if (leftScore > rightScore) return left;

  const leftLength = left.message?.length ?? 0;
  const rightLength = right.message?.length ?? 0;
  return rightLength > leftLength ? right : left;
}

function normalizeUnknownAgentError(args: {
  value: unknown;
  depth: number;
  seenRecords: Set<object>;
}): PartialNormalizedAgentErrorDetails | null {
  if (args.depth >= MAX_ERROR_NORMALIZATION_DEPTH) {
    return {
      message: asNonEmptyString(args.value),
      type: null,
      code: null,
    };
  }

  const directString = asNonEmptyString(args.value);
  if (directString) {
    const parsed = tryParseEmbeddedJson(directString);
    if (parsed !== null) {
      const normalizedParsed = normalizeUnknownAgentError({
        value: parsed,
        depth: args.depth + 1,
        seenRecords: args.seenRecords,
      });
      if (normalizedParsed?.message) {
        return normalizedParsed;
      }
    }

    return {
      message: directString,
      type: null,
      code: null,
    };
  }

  if (Array.isArray(args.value)) {
    for (const entry of args.value) {
      const normalizedEntry = normalizeUnknownAgentError({
        value: entry,
        depth: args.depth + 1,
        seenRecords: args.seenRecords,
      });
      if (normalizedEntry?.message) {
        return normalizedEntry;
      }
    }

    return null;
  }

  const record = asRecord(args.value);
  if (!record) return null;
  if (args.seenRecords.has(record)) return null;
  args.seenRecords.add(record);

  const outer: PartialNormalizedAgentErrorDetails = {
    message: null,
    type:
      asNonEmptyString(record.type) ??
      asNonEmptyString(record.name) ??
      asNonEmptyString(record.errorType),
    code:
      asCodeString(record.code) ??
      asCodeString(record.errorCode) ??
      asCodeString(record.status) ??
      asCodeString(record.statusCode),
  };

  let bestNestedCandidate: PartialNormalizedAgentErrorDetails | null = null;

  for (const key of STRUCTURED_ERROR_VALUE_KEYS) {
    const normalizedValue = normalizeUnknownAgentError({
      value: record[key],
      depth: args.depth + 1,
      seenRecords: args.seenRecords,
    });
    if (!normalizedValue) continue;
    bestNestedCandidate = pickBetterErrorDetails(
      bestNestedCandidate,
      mergeErrorDetails({
        outer,
        inner: normalizedValue,
      }),
    );
  }

  for (const key of ARRAY_ERROR_VALUE_KEYS) {
    const values = Array.isArray(record[key]) ? record[key] : [];
    for (const value of values) {
      const normalizedValue = normalizeUnknownAgentError({
        value,
        depth: args.depth + 1,
        seenRecords: args.seenRecords,
      });
      if (!normalizedValue) continue;
      bestNestedCandidate = pickBetterErrorDetails(
        bestNestedCandidate,
        mergeErrorDetails({
          outer,
          inner: normalizedValue,
        }),
      );
    }
  }

  for (const key of NESTED_ERROR_VALUE_KEYS) {
    const normalizedValue = normalizeUnknownAgentError({
        value: record[key],
        depth: args.depth + 1,
        seenRecords: args.seenRecords,
    });
    if (!normalizedValue) continue;
    bestNestedCandidate = pickBetterErrorDetails(
      bestNestedCandidate,
      mergeErrorDetails({
        outer,
        inner: normalizedValue,
      }),
    );
  }

  let bestMessageCandidate: PartialNormalizedAgentErrorDetails | null = null;
  for (const key of MESSAGE_ERROR_VALUE_KEYS) {
    const normalizedValue = normalizeUnknownAgentError({
      value: record[key],
      depth: args.depth + 1,
      seenRecords: args.seenRecords,
    });
    if (!normalizedValue) continue;
    bestMessageCandidate = pickBetterErrorDetails(
      bestMessageCandidate,
      mergeErrorDetails({
        outer,
        inner: normalizedValue,
      }),
    );
  }

  const bestCandidate = pickBetterErrorDetails(bestNestedCandidate, bestMessageCandidate);
  if (bestCandidate?.message || bestCandidate?.type || bestCandidate?.code) {
    return bestCandidate;
  }

  return outer.type || outer.code ? outer : null;
}

export function normalizeAgentErrorDetails(input: {
  value: unknown;
  fallbackMessage?: string | null;
  fallbackType?: string | null;
  fallbackCode?: string | number | null;
}): NormalizedAgentErrorDetails | null {
  const normalized = normalizeUnknownAgentError({
    value: input.value,
    depth: 0,
    seenRecords: new Set<object>(),
  });

  const message = normalized?.message ?? asNonEmptyString(input.fallbackMessage);
  if (!message) return null;

  return {
    message,
    type: normalized?.type ?? asNonEmptyString(input.fallbackType),
    code: normalized?.code ?? asCodeString(input.fallbackCode),
  };
}

export function normalizeAgentErrorMessage(input: {
  value: unknown;
  fallbackMessage?: string | null;
}): string | null {
  return (
    normalizeAgentErrorDetails({
      value: input.value,
      fallbackMessage: input.fallbackMessage,
    })?.message ?? null
  );
}
